import Button from './material/Button'
import AppButton from './material/AppButton'
import { Box, IconButton, TablePagination, Typography } from '@mui/material'
import React, { PureComponent } from 'react'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'

import Checkbox from '@mui/material/Checkbox'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import Tooltip from '@mui/material/Tooltip'
import orderBy from 'lodash/orderBy'
import { radius } from '../theme'
import {
  TABLE_BUCKET,
  getBucketRowsPerPage,
  setBucketRowsPerPage,
} from '../utils/tableBuckets'
const DEFAULTS = {
  PAGE: 0,
  ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPTIONS: [10, 25, 50, 100],
}

// Labels pt-BR pra TablePagination — extraídos pra ambas as renderizações
// (top + footer) compartilharem.
const ptBrPaginationLabels = {
  labelRowsPerPage: 'Linhas por página',
  labelDisplayedRows: ({ from, to, count }) =>
    `${from}–${to === -1 ? count : to} de ${count !== -1 ? count : to}`,
  getItemAriaLabel: (type) => {
    if (type === 'first') return 'Primeira página'
    if (type === 'last') return 'Última página'
    if (type === 'next') return 'Próxima página'
    return 'Página anterior'
  },
}

export const CELL_TYPES = {
  NOT_FOUND: 'NOT_FOUND',
  BUTTON: 'BUTTON',
  BUTTON_ARR: 'BUTTON_ARR',
  BUTTON_ICON_ARR: 'BUTTON_ICON_ARR',
  LINK: 'LINK',
  TEXT: 'TEXT',
  TEXT_CENTER: 'TEXT_CENTER',
  HOLLOW_BUTTON: 'HOLLOW_BUTTON',
  ICON_BUTTON: 'ICON_BUTTON',
  CHECKBOX: 'CHECKBOX',
  FIELD: 'FIELD',
  FLOAT_BUTTONS: 'FLOAT_BUTTONS',
}

export default class ResponsiveTable extends PureComponent {
  constructor(props) {
    super(props)
    // Bucket resolve a persistência de "linhas por página": tabelas no mesmo
    // bucket compartilham o valor escolhido (ver utils/tableBuckets.js).
    // Sem prop = TOP (default 10).
    this._bucket = props.bucket || TABLE_BUCKET.TOP
    this.state = {
      activeItems: [],
      page: this.props.page || DEFAULTS.PAGE,
      rowsPerPage: getBucketRowsPerPage(this._bucket, this.props.rowsPerPage),
      hideFirstColumn: false,
      hasTopPagination: this.props.hasTopPagination ?? true,
      sortBy: null,
      sortDirection: 'asc',
    }
    this.renderCell = this.renderCell.bind(this)
    this.changePage = this.changePage.bind(this)
    this.changeRowsPerPage = this.changeRowsPerPage.bind(this)
  }

  changePage(event, page) {
    this.setState({ page, activeItems: [] })
  }

  changeRowsPerPage(event) {
    const next = parseInt(event.target.value, 10) || DEFAULTS.ROWS_PER_PAGE
    setBucketRowsPerPage(this._bucket, next)
    this.setState({ rowsPerPage: next, page: 0, activeItems: [] })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({
        page: this.props.page || DEFAULTS.PAGE,
      })
    }
  }

  async exportData(data) {
    const str = JSON.stringify(data)
    const bytes = new TextEncoder().encode(str)
    const blob = new Blob([bytes], {
      type: 'application/json;charset=utf-8',
    })

    const newHandle = await window.showSaveFilePicker()
    const writableStream = await newHandle.createWritable()
    await writableStream.write(blob)
    await writableStream.close()
  }

  toggleVisibility(index) {
    const activeItems = this.state.activeItems.some((i) => i === index)
      ? this.state.activeItems.filter((i) => i !== index)
      : [...this.state.activeItems, index]

    this.setState({ activeItems })
  }

  renderCell(
    type,
    {
      label,
      onClick,
      onClickLink,
      buttons,
      icon,
      field,
      useGridSystem,
      checked,
      disabled,
    },
    columnName,
    index,
    tdIndex,
  ) {
    const key = `${columnName}_${index}`
    const visible = this.state.activeItems.includes(index) ? 'visible' : ''
    const params = {
      key,
      onClick,
      onClickLink,
      label,
      useGridSystem,
      checked,
      disabled,
      buttons,
      icon,
      field,
      index,
      visible,
      tdIndex,
    }

    const typesMap = new Map([
      [CELL_TYPES.NOT_FOUND, 'getNotFoundCell'],
      [CELL_TYPES.BUTTON, 'getButtonCell'],
      [CELL_TYPES.LINK, 'getLinkCell'],
      [CELL_TYPES.BUTTON_ARR, 'getButtonArrCell'],
      [CELL_TYPES.ICON_BUTTON, 'getIconButtonCell'],
      [CELL_TYPES.CHECKBOX, 'getCheckboxCell'],
      [CELL_TYPES.HOLLOW_BUTTON, 'getHollowButtonCell'],
      [CELL_TYPES.FIELD, 'getFieldCell'],
      [CELL_TYPES.TEXT_CENTER, 'getTextCenterCell'],
      [CELL_TYPES.FLOAT_BUTTONS, 'getFloatButtonsCell'],
    ])

    return !!typesMap.get(type)
      ? this[typesMap.get(type)](params)
      : this.getDefaultCell(params)
  }

  getNotFoundCell() {
    return (
      <Td colSpan={this.props.columns.length} align='center'>
        <Typography variant='h6'>
          {this.props.noDataMessage || 'Nenhum resultado'}
        </Typography>
      </Td>
    )
  }

  getButtonCell({ key, useGridSystem, onClick, label, disabled, visible }) {
    return (
      <Td key={key} align='right' className={`td-btn ${visible}`}>
        <Button
          style={useGridSystem ? { width: '100%' } : undefined}
          small
          onClick={onClick}
          label={label}
          disabled={disabled}
        />
      </Td>
    )
  }

  getLinkCell({ key, onClick, label, index, tdIndex, visible }) {
    return (
      <Td
        key={key}
        className={`td-link ${visible}`}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'var(--palette-primary-main, #EA5D0D)',
          width: this.props.columnWidth,
        }}
      >
        <span onClick={onClick}>{label}</span>
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getButtonArrCell({ key, buttons, visible }) {
    let lastChild = buttons.filter((b) => b.icon == null).length
    return (
      <Td key={key} align='right' className={`td-btn-arr ${visible}`}>
        {buttons.map((button, bIndex) =>
          button.icon ? (
            <Tooltip
              key={`${key}_${bIndex}`}
              title={button.disabled ? 'Desabilitado' : button.label}
              arrow
              placement='top'
            >
              {/*
                size="small" + drop inline padding (2026-05-11):
                size="large" (default) usava minWidth/minHeight 44px do theme,
                com inline padding: '10px' os ícones ocupavam ~44px cada +
                margin 2px = 92px mínimo pra 2 ícones. Em colunas de ação
                estreitas (tabela matrizes com 8-10 colunas competindo), eles
                quebravam linha. small = 36px (theme MuiIconButton.sizeSmall),
                sem padding inline = 36 + 4 + 36 = ~76px, cabe folgado.
              */}
              <IconButton
                disabled={!!button.disabled}
                onClick={button.onClick}
                className='td-btn-arr-icon'
                size='small'
              >
                {button.icon}
              </IconButton>
            </Tooltip>
          ) : (
            (() => {
              const isPrimary =
                button.label === 'Ressinc' || bIndex + 1 === lastChild
              return (
                <AppButton
                  key={`${key}_${bIndex}`}
                  size='small'
                  variant={isPrimary ? 'primary' : 'secondary'}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  sx={{ m: 0.5 }}
                  className='td-btn-arr-btn'
                >
                  {button.label}
                </AppButton>
              )
            })()
          ),
        )}
      </Td>
    )
  }

  getFloatButtonsCell({ key, buttons, visible }) {
    let lastChild = buttons.filter((b) => b.icon == null).length

    return (
      <Td key={key} align='right' className={`td-float-buttons ${visible}`}>
        <label htmlFor={'action_' + key}>
          <Box
            component='span'
            className='td-float-buttons-trigger'
            sx={{
              padding: '5px 8px',
              borderRadius: `${radius.md}`,
              border: '1px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            Manejos
          </Box>
          <input type='checkbox' id={'action_' + key} />
          <div>
            {buttons
              .filter(
                (i) =>
                  i.label !== 'Excluir' &&
                  i.label !== 'Editar' &&
                  i.label !== 'Sincronizar' &&
                  i.label !== 'Voltar para Ressinc anterior',
              )
              .map((button, bIndex) =>
                this.renderFloatingIcons(key, bIndex, button, lastChild),
              )}
          </div>
        </label>

        {buttons
          .filter(
            (i) =>
              i.label === 'Excluir' ||
              i.label === 'Editar' ||
              i.label === 'Sincronizar' ||
              i.label === 'Voltar para Ressinc anterior',
          )
          .map((button, bIndex) => this.renderActionIcons(key, bIndex, button))}
      </Td>
    )
  }

  getIconButtonCell({ key, onClick, label, icon, visible, disabled }) {
    return (
      <Td key={`${key}`} align='right' className={`td-icon-btn ${visible}`}>
        <Tooltip title={label} arrow placement='top'>
          {/* MUI v5+: Tooltip não recebe eventos de elementos disabled.
              Wrap em <span> permite o tooltip exibir mesmo com IconButton disabled. */}
          <span>
            <IconButton
              key={`${key}`}
              disabled={disabled}
              onClick={onClick}
              size='small'
            >
              {icon}
            </IconButton>
          </span>
        </Tooltip>
      </Td>
    )
  }

  getCheckboxCell({
    key,
    onClick,
    onClickLink,
    checked,
    disabled,
    visible,
    label,
    index,
    tdIndex,
  }) {
    return (
      <Td
        key={`${key}`}
        className={`td-link ${visible}`}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'var(--palette-primary-main, #EA5D0D)',
          width: this.props.columnWidth,
        }}
      >
        <Checkbox
          onClick={onClick}
          checked={checked}
          disabled={disabled}
          color='primary'
          style={{ marginRight: '10px' }}
        />
        <span onClick={onClickLink}>{label}</span>
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getHollowButtonCell({ key, onClick, icon, visible }) {
    return (
      <Td key={`${key}`} align='right' className={`td-hollow ${visible}`}>
        <IconButton onClick={onClick} size='small'>
          {icon}
        </IconButton>
      </Td>
    )
  }

  getFieldCell({ key, field, index, tdIndex, visible }) {
    return (
      <Td key={`${key}`} className={`td-field ${visible}`}>
        {field}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getTextCenterCell({ key, label, index, tdIndex, visible }) {
    return (
      <Td key={`${key}`} align='center' className={`td-text-center ${visible}`}>
        {label}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  getDefaultCell({ key, label, index, tdIndex, visible }) {
    return (
      <Td
        key={`${key}`}
        style={{ width: this.props.columnWidth }}
        className={`td-default ${visible}`}
      >
        {label}
        {tdIndex === 0 && (
          <span
            className='collapse-icon'
            onClick={() => this.toggleVisibility(index)}
          >
            <KeyboardArrowUpIcon />
          </span>
        )}
      </Td>
    )
  }

  renderFloatingIcons(key, bIndex, button, lastChild) {
    const isPrimary = bIndex + 1 === lastChild
    return (
      <AppButton
        key={`${key}_${bIndex}`}
        size='small'
        variant={isPrimary ? 'primary' : 'secondary'}
        onClick={button.onClick}
        disabled={button.disabled}
        sx={{ m: 0.5 }}
        className='td-btn-arr-btn'
      >
        {button.label}
      </AppButton>
    )
  }

  renderActionIcons(key, bIndex, button) {
    return button.icon ? (
      <Tooltip
        key={`${key}_${bIndex}`}
        title={button.disabled ? 'Desabilitado' : button.label}
        arrow
        placement='top'
      >
        <IconButton
          style={{ color: button.disabled && '#cecece' }}
          onClick={button.disabled ? null : button.onClick}
          className='td-btn-arr-icon'
          size='small'
        >
          {button.icon}
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        key={`${key}_${bIndex}`}
        small
        onClick={button.onClick}
        label={button.label}
        disabled={button.disabled}
        className='td-btn-arr-btn'
      />
    )
  }

  renderTableItem(useGridSystem, row, i) {
    return (
      <Tr key={i}>
        {this.props.columns.map((column, tdIndex) =>
          this.renderCell(
            column.type,
            {
              label: column.label ? column.label(row, i) : '',
              onClick: column.onClick && column.onClick(row, i),
              onClickLink: column.onClickLink && column.onClickLink(row, i),
              buttons: column.buttons ? column.buttons(row, i) : [],
              icon: column.icon && column.icon(row, i),
              field: column.field && column.field(row, i),
              checked: column.checked && column.checked(row, i),
              disabled: column.disabled && column.disabled(row, i),
              useGridSystem,
            },
            column.name,
            i,
            tdIndex,
          ),
        )}
      </Tr>
    )
  }

  renderTopTablePagination() {
    return (
      <div className='top-table-pagination'>
        {/* TablePagination por default renderiza um <td> (component="td").
            Aqui está fora de qualquer <table>, então passamos component="div"
            para evitar HTML inválido (<div> contendo <td>) — React 19 valida
            estrito e logaria warning no console. */}
        <TablePagination
          component='div'
          count={this.props.data.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.state.page}
          onPageChange={this.changePage}
          onRowsPerPageChange={this.changeRowsPerPage}
          rowsPerPageOptions={
            this.props.rowsPerPageOptions || DEFAULTS.ROWS_PER_PAGE_OPTIONS
          }
          {...ptBrPaginationLabels}
        />
      </div>
    )
  }

  renderTableFooter() {
    return (
      <tfoot>
        <tr>
          <TablePagination
            colSpan={this.props.columns.length}
            count={this.props.data.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.state.page}
            onPageChange={this.changePage}
            onRowsPerPageChange={this.changeRowsPerPage}
            rowsPerPageOptions={
              this.props.rowsPerPageOptions || DEFAULTS.ROWS_PER_PAGE_OPTIONS
            }
            {...ptBrPaginationLabels}
          />
        </tr>
      </tfoot>
    )
  }

  handleSort = (column) => {
    const { sortBy, sortDirection } = this.state
    let newDirection

    if (sortBy === column.name) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      newDirection = 'asc'
    }

    // Mantém callback opcional pro pai (caso queira saber a ordem). O sort
    // efetivo, porém, é aplicado localmente no render usando sortBy/sortDirection
    // do state — não dependemos mais do pai propagar onSort.
    if (this.props.onSort) {
      const sortedData = orderBy(
        this.props.data,
        [(row) => column.label(row)],
        [newDirection],
      )
      this.props.onSort(sortedData)
    }

    this.setState({
      sortBy: column.name,
      sortDirection: newDirection,
    })
  }

  render() {
    let useGridSystem = false

    // Aplica sort local quando o usuário clicou num cabeçalho com `sortBy: true`.
    // sortValue (custom) tem prioridade sobre label (fallback) para suportar
    // ordenação semântica em colunas formatadas (datas, números com unidade etc.).
    //
    // pinLastRow: a última linha (ex: linha de Totais nos relatórios) é fixada
    // como último elemento — separada antes do sort e reanexada no final.
    const { sortBy, sortDirection } = this.state
    const sortColumn = sortBy
      ? this.props.columns.find((c) => c.name === sortBy)
      : null
    let sourceData
    if (sortColumn) {
      const rows = this.props.data
      const pinned =
        this.props.pinLastRow && rows.length > 0 ? rows[rows.length - 1] : null
      const sortable = pinned ? rows.slice(0, -1) : rows
      const sorted = orderBy(
        sortable,
        [
          (row, i) =>
            sortColumn.sortValue
              ? sortColumn.sortValue(row, i)
              : sortColumn.label(row, i),
        ],
        [sortDirection],
      )
      sourceData = pinned ? [...sorted, pinned] : sorted
    } else {
      sourceData = this.props.data
    }

    const data = !this.props.skipPagination
      ? sourceData.slice(
          this.state.page * this.state.rowsPerPage,
          (this.state.page + 1) * this.state.rowsPerPage,
        )
      : sourceData

    return (
      <>
        {this.state.hasTopPagination && this.renderTopTablePagination()}
        {/* Wrapper com scroll horizontal: necessário em telas estreitas com muitas colunas
             para evitar que a tabela ultrapasse o container pai. Em mobile a tabela
             colapsa para cards (responsiveTable plugin) e o overflow vira no-op. */}
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <Table
            className={'app-responsive-table ' + this.props.className}
            style={{
              margin: 0,
              // Bottom corners arredondados (token md) — top fica reto porque
              // o wrapper .top-table-pagination já tem o radius top.
              borderRadius: `0 0 ${radius.md}px ${radius.md}px`,
            }}
          >
            <Thead>
              <Tr>
                {this.props.columns.map((column, index) => {
                  return (
                    <Th
                      key={`${column.name}_${index}`}
                      onClick={() => column.sortBy && this.handleSort(column)}
                      style={{ cursor: column.sortBy ? 'pointer' : 'default' }}
                    >
                      <div className='column-header'>
                        {column.name}
                        {column.sortBy && (
                          <SwapVertIcon
                            className='sortable-column'
                            style={{ fontSize: 18, marginLeft: 5 }}
                          />
                        )}
                      </div>
                    </Th>
                  )
                })}
              </Tr>
            </Thead>
            <Tbody>
              {!this.props.data.length ? (
                <Tr>{this.renderCell('NOT_FOUND', {})}</Tr>
              ) : (
                data.map((row, i) =>
                  this.renderTableItem(useGridSystem, row, i),
                )
              )}
            </Tbody>

            {!this.props.skipPagination && this.renderTableFooter()}
          </Table>
        </div>
      </>
    )
  }
}
